import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { changeVehicleSelectors, fetchCatalogMakes, fetchCatalogModels, fetchCatalogTrims, fetchCatalogYears } from '../../store';
import type { BuildVehicleInitValue, YMMTSelects } from './types';
import { BuildVehicleSelects, InventoryTypeFilters, selectOrder } from './types';
import { findCaseInsensitiveMatch } from './utils';

const useBuildVehicleSelectValues = (initValue: BuildVehicleInitValue) => {
    const dispatch = useDispatch();

    const [vin, setVin] = useState<string | undefined>(undefined);
    const [isInitialized, setIsInitialized] = useState(false);
    const [selectValues, setSelectValues] = useState<Record<BuildVehicleSelects, string>>({
        [BuildVehicleSelects.inventoryType]: '',
        [BuildVehicleSelects.year]: '',
        [BuildVehicleSelects.make]: '',
        [BuildVehicleSelects.model]: '',
        [BuildVehicleSelects.trim]: ''
    });

    const yearOptions = useSelector(changeVehicleSelectors.getCatalogYears);
    const makeOptions = useSelector(changeVehicleSelectors.getCatalogMakes);
    const modelOptions = useSelector(changeVehicleSelectors.getCatalogModels);
    const trimOptions = useSelector(changeVehicleSelectors.getCatalogTrims);
    const bffEndpoint = useSelector(changeVehicleSelectors.additionalSelectors.getServicesBff);

    const initializeWithValueFromOptions = (selectName: YMMTSelects, options: string[]) => {
        if (isInitialized) return;

        const matchedValue = findCaseInsensitiveMatch(options, initValue[selectName]);
        const isLastStep = selectOrder.indexOf(selectName) === selectOrder.length - 1;

        if (matchedValue) {
            setSelectValues((prevState) => ({ ...prevState, [selectName]: matchedValue }));

            if (isLastStep) {
                setIsInitialized(true);
            }
        }
    };

    useEffect(() => {
        initializeWithValueFromOptions(BuildVehicleSelects.year, yearOptions);
    }, [yearOptions]);

    useEffect(() => {
        initializeWithValueFromOptions(BuildVehicleSelects.make, makeOptions);
    }, [makeOptions]);

    useEffect(() => {
        initializeWithValueFromOptions(BuildVehicleSelects.model, modelOptions);
    }, [modelOptions]);

    useEffect(() => {
        initializeWithValueFromOptions(BuildVehicleSelects.trim, trimOptions);
    }, [trimOptions]);

    useEffect(() => {
        if (!selectValues.year) return;
        dispatch(fetchCatalogMakes(bffEndpoint, selectValues.year));
    }, [selectValues.year]);

    useEffect(() => {
        if (!selectValues.make) return;
        dispatch(fetchCatalogModels(bffEndpoint, selectValues.year, selectValues.make));
    }, [selectValues.make]);

    useEffect(() => {
        if (!selectValues.model) return;
        dispatch(fetchCatalogTrims(bffEndpoint, selectValues.year, selectValues.make, selectValues.model));
    }, [selectValues.model]);

    useEffect(() => {
        dispatch(fetchCatalogYears(bffEndpoint));
        setVin(initValue.vin);
        setSelectValues((prevState) => ({
            ...prevState,
            [BuildVehicleSelects.inventoryType]: initValue.inventoryType || InventoryTypeFilters.NEW
        }));
    }, [initValue, dispatch, bffEndpoint]);

    return { vin, setVin, isInitialized, setIsInitialized, selectValues, setSelectValues };
};

export default useBuildVehicleSelectValues;
